import React, { useEffect, useRef } from 'react';
import { createChart, ColorType, Time, HistogramData, LineData, HistogramSeries, LineSeries, ISeriesApi } from 'lightweight-charts';
import { MacdData } from '../../api/metrics';
import { TimezoneMode, formatCrosshairLabel, formatTickMarkLabel } from '../../utils/timezone';
import { getDefaultVisibleCandles } from '../../utils/chartZoom';

interface MacdPanelProps {
  data: MacdData[];
  loading?: boolean;
  timezone?: TimezoneMode;
  granularityMins?: number;
}

/**
 * MACD Indicator Panel Component
 * Displays MACD histogram, MACD line, and signal line below the price chart
 * Synchronized with master chart for zoom/pan
 */
const MacdPanel: React.FC<MacdPanelProps> = ({ data, loading, timezone = 'UTC', granularityMins = 60 }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const histogramSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const macdLineSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);
  const signalLineSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);

  useEffect(() => {
    if (!chartContainerRef.current || loading || !data || data.length === 0) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#94a3b8',
      },
      grid: {
        vertLines: { color: 'rgba(148, 163, 184, 0.1)' },
        horzLines: { color: 'rgba(148, 163, 184, 0.2)' },
      },
      crosshair: {
        mode: 0,
        vertLine: {
          width: 1,
          color: '#8B5CF6',
          style: 1,
          labelBackgroundColor: '#8B5CF6',
        },
        horzLine: {
          width: 1,
          color: '#8B5CF6',
          style: 1,
          labelBackgroundColor: '#8B5CF6',
        },
      },
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
        borderColor: '#475569',
        tickMarkFormatter: (time: Time, tickMarkType: number) =>
          formatTickMarkLabel(time as number, tickMarkType, timezone),
      },
      localization: {
        timeFormatter: (t: number) => formatCrosshairLabel(t, timezone),
      },
      rightPriceScale: {
        borderColor: '#475569',
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
    });

    chartRef.current = chart;

    // Add histogram series for MACD histogram
    const histogramSeries = chart.addSeries(HistogramSeries, {
      priceFormat: {
        type: 'price',
        precision: 4,
        minMove: 0.0001,
      },
    });
    histogramSeriesRef.current = histogramSeries;

    // Add MACD line series
    const macdLineSeries = chart.addSeries(LineSeries, {
      color: '#00BCD4',
      lineWidth: 2,
      title: 'MACD',
      priceLineVisible: false,
      lastValueVisible: true,
    });
    macdLineSeriesRef.current = macdLineSeries;

    // Add signal line series
    const signalLineSeries = chart.addSeries(LineSeries, {
      color: '#FF5722',
      lineWidth: 2,
      title: 'Signal',
      priceLineVisible: false,
      lastValueVisible: true,
    });
    signalLineSeriesRef.current = signalLineSeries;

    // Convert MACD data to chart format
    const histogramData: HistogramData<Time>[] = [];
    const macdLineData: LineData<Time>[] = [];
    const signalLineData: LineData<Time>[] = [];

    data.forEach(point => {
      if (!point.complete) return;
      
      const time = (new Date(point.metricTime).getTime() / 1000) as Time;
      const histogram = Number(point.histogram);
      const macdLine = Number(point.macdLine);
      const signalLine = Number(point.signalLine);

      // Histogram colored based on positive/negative
      histogramData.push({
        time,
        value: histogram,
        color: histogram >= 0 ? 'rgba(76, 175, 80, 0.6)' : 'rgba(244, 67, 54, 0.6)',
      });

      macdLineData.push({ time, value: macdLine });
      signalLineData.push({ time, value: signalLine });
    });

    // Set data on series
    histogramSeries.setData(histogramData);
    macdLineSeries.setData(macdLineData);
    signalLineSeries.setData(signalLineData);

      // Configure chart to auto-scale and fit all data
      chart.priceScale('right').applyOptions({
          scaleMargins: {
              top: 0.1,
              bottom: 0.1,
          },
          autoScale: true,
      });

      // Set visible range based on granularity to match main chart

      const defaultVisibleCandles = getDefaultVisibleCandles(granularityMins);
      const totalCandles = histogramData.length;

      if (totalCandles > 0) {
        const from = Math.max(0, totalCandles - defaultVisibleCandles);
        const to = totalCandles - 1;
        chart.timeScale().setVisibleLogicalRange({ from, to });
      }

    // Add zero line as a price line
    histogramSeries.createPriceLine({
      price: 0,
      color: '#64748b',
      lineWidth: 1,
      lineStyle: 2, // Dashed
      axisLabelVisible: false,
      title: '',
    });

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
      chartRef.current = null;
      histogramSeriesRef.current = null;
      macdLineSeriesRef.current = null;
      signalLineSeriesRef.current = null;
    };
  }, [loading, data]);

  // Apply timezone changes without recreating chart
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.applyOptions({
      localization: {
        timeFormatter: (t: number) => formatCrosshairLabel(t, timezone),
      },
      timeScale: {
        tickMarkFormatter: (time: Time, tickMarkType: number) =>
          formatTickMarkLabel(time as number, tickMarkType, timezone),
      },
    });
  }, [timezone]);


  if (loading) {
    return (
      <div style={{ 
        height: 150, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#94a3b8',
        backgroundColor: 'rgba(30, 41, 59, 0.3)',
        borderRadius: '4px',
      }}>
        Loading MACD data...
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{ 
        height: 150, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#94a3b8',
        backgroundColor: 'rgba(30, 41, 59, 0.3)',
        borderRadius: '4px',
      }}>
        No MACD data available
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* MACD Panel Title */}
      <div style={{
        padding: '8px 12px',
        fontSize: '12px',
        fontWeight: 700,
        color: '#8B5CF6',
        backgroundColor: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '4px 4px 0 0',
        borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
        textTransform: 'uppercase',
        letterSpacing: '1px',
      }}>
        MACD (12, 26, 9)
      </div>

      {/* Chart Container */}
      <div 
        ref={chartContainerRef}
        style={{ 
          width: '100%', 
          flex: 1,
          minHeight: 0,
          position: 'relative',
          backgroundColor: 'rgba(30, 41, 59, 0.3)',
          borderRadius: '0 0 4px 4px',
        }}
      />
    </div>
  );
};

export default MacdPanel;
